const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const normalize = (value) => String(value ?? '').trim().toLowerCase()

const asArray = (value) => (Array.isArray(value) ? value : [])

const addReference = (references, prefix, value) => {
  const normalizedValue = normalize(value)
  if (normalizedValue) references.add(`${prefix}:${normalizedValue}`)
}

const getStrongFamilyReferences = (family = {}) => {
  const references = new Set()

  addReference(references, 'family', family.familyId)
  addReference(references, 'family', family.id)
  addReference(references, 'family', family._id)
  addReference(references, 'wave', family.waveCustomerId)
  addReference(references, 'wave', family.familyWaveId)

  return references
}

const referencesOverlap = (leftReferences, rightReferences) => {
  for (const reference of leftReferences) {
    if (rightReferences.has(reference)) return true
  }
  return false
}

const familiesMatch = (currentFamily, timesheetFamily) => {
  const currentReferences = getStrongFamilyReferences(currentFamily)
  const timesheetReferences = getStrongFamilyReferences(timesheetFamily)

  // When both records have stable identifiers, a matching name must not join
  // two different families that happen to share a last name.
  if (currentReferences.size && timesheetReferences.size) {
    return referencesOverlap(currentReferences, timesheetReferences)
  }

  const currentName = normalize(currentFamily?.familyName)
  const timesheetName = normalize(timesheetFamily?.familyName)
  return Boolean(currentName && currentName === timesheetName)
}

const getStudentId = (student = {}) => normalize(student.studentId || student.studentID)

const getStudentName = (student = {}) => normalize(
  student.studentName || student.student || student.name
)

const isActiveStudent = (student) => normalize(student?.studentStatus) === 'active'

const getTimesheetStudentEntries = (timesheetFamily = {}) => [
  ...asArray(timesheetFamily.lessons),
  ...asArray(timesheetFamily.cancellations),
].filter((entry) => getStudentId(entry) || getStudentName(entry))

const timesheetEntryMatchesStudent = (entry, currentStudent, timesheetFamily) => {
  const entryStudentId = getStudentId(entry)
  const currentStudentId = getStudentId(currentStudent)

  if (entryStudentId && currentStudentId) return entryStudentId === currentStudentId

  const entryName = getStudentName(entry)
  const currentName = getStudentName(currentStudent)
  if (entryName && currentName && entryName === currentName) return true

  // Older timesheet rows frequently store only a name. If the row can be
  // resolved unambiguously through its family snapshot, use that stable ID.
  if (entryName && currentStudentId) {
    const matchingSnapshotStudents = asArray(timesheetFamily.listOfFamilyStudents)
      .filter((student) => getStudentName(student) === entryName)
    return matchingSnapshotStudents.length === 1
      && getStudentId(matchingSnapshotStudents[0]) === currentStudentId
  }

  return false
}

const studentAppearsOnTimesheet = (family, student, timesheets) => asArray(timesheets)
  .some((timesheet) => asArray(timesheet?.families).some((timesheetFamily) => {
    if (!familiesMatch(family, timesheetFamily)) return false

    return getTimesheetStudentEntries(timesheetFamily)
      .some((entry) => timesheetEntryMatchesStudent(entry, student, timesheetFamily))
  }))

export const getDefaultBillingPeriod = (today = new Date(), cutoffDay = 15) => {
  const selectedDate = new Date(today)

  if (Number.isNaN(selectedDate.getTime())) {
    throw new Error('A valid date is required to determine the billing period.')
  }

  if (selectedDate.getDate() < cutoffDay) {
    selectedDate.setMonth(selectedDate.getMonth() - 1)
  }

  return {
    month: MONTH_NAMES[selectedDate.getMonth()],
    monthNumber: selectedDate.getMonth() + 1,
    year: String(selectedDate.getFullYear()),
  }
}

export const getCurrentCalendarPeriod = (today = new Date()) => {
  const selectedDate = new Date(today)

  if (Number.isNaN(selectedDate.getTime())) {
    throw new Error('A valid date is required to determine the calendar period.')
  }

  return {
    month: MONTH_NAMES[selectedDate.getMonth()],
    monthNumber: selectedDate.getMonth() + 1,
    year: String(selectedDate.getFullYear()),
  }
}

export const parseAllTimesheetDataResponse = (data) => {
  if (Array.isArray(data)) {
    return {
      timesheets: asArray(data[0]),
      invoices: asArray(data[1]),
      activeFamilies: asArray(data[2]),
    }
  }

  return {
    timesheets: asArray(data?.timesheets),
    invoices: asArray(data?.invoices),
    activeFamilies: asArray(data?.activeFamilies || data?.students),
  }
}

const buildStudentsWithoutLessonsLookup = (studentsWithoutLessons) => {
  if (!Array.isArray(studentsWithoutLessons)) return null

  const studentIds = new Set()

  studentsWithoutLessons.forEach((student) => {
    const studentId = getStudentId(student)
    if (studentId) studentIds.add(studentId)
  })

  return { studentIds }
}

const studentHasNoCurrentMonthLessons = (student, lookup) => {
  if (!lookup) return true

  const studentId = getStudentId(student)
  // The calendar endpoint identifies events by studentId. Keep legacy students
  // without an ID visible rather than incorrectly assuming they are scheduled.
  if (!studentId) return true

  return lookup.studentIds.has(studentId)
}

export const filterFamiliesWithoutCurrentMonthLessons = (families, studentsWithoutCurrentMonthLessons = null) => {
  const studentsWithoutLessonsLookup = buildStudentsWithoutLessonsLookup(studentsWithoutCurrentMonthLessons)

  return asArray(families)
    .map((family) => ({
      ...family,
      students: asArray(family?.students)
        .filter((student) => studentHasNoCurrentMonthLessons(student, studentsWithoutLessonsLookup)),
    }))
    .filter((family) => family.students.length > 0)
}

const getFamilyStudentKey = (family, student) => {
  const studentId = getStudentId(student)
  if (studentId) return `student:${studentId}`

  const familyReference = normalize(family?.familyId || family?.id || family?._id || family?.familyName)
  const studentName = getStudentName(student)
  return familyReference && studentName ? `family:${familyReference}|student:${studentName}` : ''
}

export const excludeStudentsAlreadyListed = (families, excludedFamilies) => {
  const excludedStudentKeys = new Set()

  asArray(excludedFamilies).forEach((family) => {
    asArray(family?.students).forEach((student) => {
      const key = getFamilyStudentKey(family, student)
      if (key) excludedStudentKeys.add(key)
    })
  })

  return asArray(families)
    .map((family) => ({
      ...family,
      students: asArray(family?.students).filter((student) => {
        const key = getFamilyStudentKey(family, student)
        return !key || !excludedStudentKeys.has(key)
      }),
    }))
    .filter((family) => family.students.length > 0)
}

export const findUnbilledActiveStudents = (activeFamilies, timesheets, studentsWithoutCurrentMonthLessons = null) => {
  const unbilledFamilies = asArray(activeFamilies)
  .map((family) => {
    const unbilledStudents = asArray(family?.students)
      .filter(isActiveStudent)
      .filter((student) => !studentAppearsOnTimesheet(family, student, timesheets))
      .sort((left, right) => String(left?.studentName || '').localeCompare(String(right?.studentName || '')))

    return {
      ...family,
      students: unbilledStudents,
    }
  })
  .filter((family) => family.students.length > 0)
  .sort((left, right) => String(left?.familyName || '').localeCompare(String(right?.familyName || '')))

  return filterFamiliesWithoutCurrentMonthLessons(
    unbilledFamilies,
    studentsWithoutCurrentMonthLessons
  )
}

export const countUnbilledActiveStudents = (families) => asArray(families)
  .reduce((total, family) => total + asArray(family?.students).length, 0)
